// Timezone utility for calculating time based on delivery location timezone

// Map of US states to their primary timezone
const STATE_TIMEZONES: Record<string, string> = {
  // Eastern Time
  'CT': 'America/New_York',
  'DE': 'America/New_York',
  'FL': 'America/New_York',
  'GA': 'America/New_York',
  'ME': 'America/New_York',
  'MD': 'America/New_York',
  'MA': 'America/New_York',
  'NH': 'America/New_York',
  'NJ': 'America/New_York',
  'NY': 'America/New_York',
  'NC': 'America/New_York',
  'OH': 'America/New_York',
  'PA': 'America/New_York',
  'RI': 'America/New_York',
  'SC': 'America/New_York',
  'VT': 'America/New_York',
  'VA': 'America/New_York',
  'WV': 'America/New_York',
  
  // Central Time
  'AL': 'America/Chicago',
  'AR': 'America/Chicago',
  'IL': 'America/Chicago',
  'IA': 'America/Chicago',
  'KS': 'America/Chicago',
  'KY': 'America/Chicago',
  'LA': 'America/Chicago',
  'MN': 'America/Chicago',
  'MS': 'America/Chicago',
  'MO': 'America/Chicago',
  'NE': 'America/Chicago',
  'ND': 'America/Chicago',
  'OK': 'America/Chicago',
  'SD': 'America/Chicago',
  'TN': 'America/Chicago',
  'TX': 'America/Chicago',
  'WI': 'America/Chicago',
  
  // Mountain Time
  'AZ': 'America/Phoenix', // Arizona doesn't observe DST
  'CO': 'America/Denver',
  'ID': 'America/Denver',
  'MT': 'America/Denver',
  'NM': 'America/Denver',
  'UT': 'America/Denver',
  'WY': 'America/Denver',
  
  // Pacific Time
  'CA': 'America/Los_Angeles',
  'NV': 'America/Los_Angeles',
  'OR': 'America/Los_Angeles',
  'WA': 'America/Los_Angeles',
  
  // Alaska Time
  'AK': 'America/Anchorage',
  
  // Hawaii Time
  'HI': 'America/Honolulu',
};

/**
 * Get the timezone for a given state
 */
export function getTimezoneForState(state: string): string {
  const stateCode = state.toUpperCase().trim();
  return STATE_TIMEZONES[stateCode] || 'America/New_York'; // Default to Eastern
}

/**
 * Get current time in a specific timezone
 */
export function getCurrentTimeInTimezone(timezone: string): Date {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  
  const parts = formatter.formatToParts(now);
  const year = parseInt(parts.find(p => p.type === 'year')?.value || '0');
  const month = parseInt(parts.find(p => p.type === 'month')?.value || '0') - 1;
  const day = parseInt(parts.find(p => p.type === 'day')?.value || '0');
  const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
  const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
  const second = parseInt(parts.find(p => p.type === 'second')?.value || '0');
  
  return new Date(year, month, day, hour, minute, second);
}

/**
 * Calculate time remaining until delivery deadline in the delivery location's timezone
 */
export function calculateTimeUntilDelivery(
  deliveryDateStr: string,
  deliveryState: string
): { hours: number; minutes: number; isOverdue: boolean; totalMinutes: number } {
  try {
    const deliveryTimezone = getTimezoneForState(deliveryState);
    
    // Get current time in delivery location's timezone
    const nowInDeliveryTz = getCurrentTimeInTimezone(deliveryTimezone);
    
    // Parse delivery date (assuming it's stored in UTC or local format)
    const deliveryDate = new Date(deliveryDateStr);
    
    // Calculate difference in milliseconds
    const diffMs = deliveryDate.getTime() - nowInDeliveryTz.getTime();
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    
    const isOverdue = diffMs < 0;
    const absTotalMinutes = Math.abs(totalMinutes);
    
    const hours = Math.floor(absTotalMinutes / 60);
    const minutes = absTotalMinutes % 60;
    
    return {
      hours,
      minutes,
      isOverdue,
      totalMinutes: isOverdue ? -absTotalMinutes : absTotalMinutes,
    };
  } catch (error) {
    console.error('Error calculating time until delivery:', error);
    return { hours: 0, minutes: 0, isOverdue: false, totalMinutes: 0 };
  }
}

/**
 * Format time remaining as a readable string
 */
export function formatTimeRemaining(
  hours: number,
  minutes: number,
  isOverdue: boolean
): string {
  if (isOverdue) {
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h LATE`;
    }
    return `${hours}h LATE`;
  }
  
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h remaining`;
  }
  
  if (hours > 0) {
    return `${hours}h ${minutes}m remaining`;
  }
  
  return `${minutes}m remaining`;
}
